import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from 'components/Button/Button';
import { ModalActions, ModalContent } from 'components/Modal/Modal';
import VideoPlayer from 'components/VideoPlayer';
import WithFeature from 'components/WithFeature';
import WorkingHoursSelect, {
  timeValidator,
} from 'components/WorkingHoursSelect/WorkingHoursSelect';
import Form from 'components/form';
import Checkbox from 'components/form/Checkbox';
import Select from 'components/form/Select';
import Switch from 'components/form/Switch';
import TextInput from 'components/form/TextInput';
import TextField from 'components/form/TextInput';
import TimePicker, { dateToMinutes } from 'components/form/TimePicker';
import FEATURES from 'constants/features';
import { EMPTY_TIME } from 'constants/index';
import useFeature from 'hooks/useFeature';
import useRequest from 'hooks/useRequest';
import { callHoursToMap, createCallHourGroups } from 'modules/phoneapp/utils';
import moment from 'moment/moment';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { updateConnection } from 'services/account.service';
import {
  getCallHours,
  getOperators,
  getReceptionists,
} from 'services/ivr.service';
import { getConnections } from 'store/modules/settings/actions';
import { formatPhoneNumber, toUserTimezone } from 'utils';
import toast from 'utils/toast';

const { FormItem } = Form;

const minutesToTUCWithZone = (minutes, timezone) => {
  const date = moment.tz(timezone).startOf('day');
  date.minutes(minutes);
  return date.utc().format('HH:mm:ss');
};

const Edit = ({ form, connection, onCancel, onSave }) => {
  const account = useSelector((s) => s.settings.account);
  const [t] = useTranslation('settings');
  const dispatch = useDispatch();
  const hasTalk = useFeature(FEATURES.TALK_DIGITAL_RECEPTION);
  const [receptionists, setReceptionists] = useState([]);
  const [callHourGroup, setCallHourGroup] = useState(); // caller
  const [callHours, setCallHours] = useState([]); // human_receptionist
  const [operators, { loading }] = useRequest(getOperators, {
    account_id: account.account_id,
  });
  const {
    validateFields,
    getFieldDecorator,
    getFieldValue,
    getFieldsValue,
    setFieldsValue,
  } = form;

  const handleSubmit = () => {
    return validateFields()
      .then(async (values) => {
        if (!values.fwd_human_receptionist) {
          values.human_receptionist_contact_id = '0';
        }
        values.fwd_human_receptionist = values.fwd_human_receptionist
          ? '1'
          : '0';
        if (!forwardToDR) {
          values.digital_receptionist_id = '0';
        }

        values.incoming_sms_enabled = values.incoming_sms_enabled ? '1' : '0';
        values.incoming_calls_enabled = values.incoming_calls_enabled
          ? '1'
          : '0';
        values.hash = connection.hash;

        if (values.connection_away_message_enabled) {
          values.connection_away_start_time = minutesToTUCWithZone(
            values.connection_away_start_time,
            account.account_timezone,
          );
          values.connection_away_end_time = minutesToTUCWithZone(
            values.connection_away_end_time,
            account.account_timezone,
          );
        } else {
          values.connection_away_start_time = EMPTY_TIME;
          values.connection_away_end_time = EMPTY_TIME;
        }
        values.connection_away_message_enabled =
          values.connection_away_message_enabled ? '1' : '0';

        try {
          await updateConnection(values);

          if (values.hours && values.fwd_human_receptionist === '1') {
            await createCallHourGroups(
              'human_receptionist',
              connection.conn_id,
              values.hours,
              callHours,
            );
          }

          await dispatch(getConnections({ account_id: account.account_id }));
          onSave();
        } catch (e) {
          toast.error(e.message);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  };

  useEffect(() => {
    getReceptionists({ account_id: account.account_id }).then((r) =>
      setReceptionists(
        r.map((i) => ({
          label: i.digital_receptionist_name,
          value: i.digital_receptionist_id,
        })),
      ),
    );
    getCallHours({
      object_type: 'human_receptionist',
      object_id: connection.conn_id,
    }).then((resp) => {
      setCallHours(resp);
      const hours = callHoursToMap(resp);
      setFieldsValue({
        hours,
      });
    });
  }, []);

  const onCallerChange = (val) => {
    if (hasTalk) {
      const op = operators.find((i) => i.contact_id === val);
      getCallHours({
        caller_id: op.caller_id,
      }).then((resp) => {
        resp.forEach((r) => {
          delete r.hash;
        });
        const hours = callHoursToMap(resp);
        setCallHourGroup(resp);
        setFieldsValue({
          hours,
        });
      });
    }
  };

  const validateAwayMessage = (rule, value, callback) => {
    const { connection_away_message_enabled } = getFieldsValue();
    if (connection_away_message_enabled && !value) {
      callback(new Error(rule.message));
    } else {
      callback();
    }
  };

  getFieldDecorator('hours');
  const forwardToDR =
    getFieldValue('forwardToDR') ?? connection.digital_receptionist_id !== '0';
  const fwd_human_receptionist =
    getFieldValue('fwd_human_receptionist') ??
    connection.fwd_human_receptionist === '1';

  return (
    <>
      <ModalContent>
        <Typography>{formatPhoneNumber(connection.conn_address)}</Typography>
        <FormItem>
          {getFieldDecorator('connection_name', {
            initialValue: connection.connection_name,
            rules: [{ required: true, message: t('common:field-required') }],
          })(
            <TextInput
              fullWidth
              label={t('connection-name', 'Name for this number')}
            />,
          )}
        </FormItem>
        <FormItem>
          {getFieldDecorator('conn_compliance_name', {
            initialValue: connection.conn_compliance_name,
            rules: [{ required: true, message: t('common:field-required') }],
          })(
            <TextInput
              fullWidth
              label={t('connection-compliance-name', 'Public Display Name')}
            />,
          )}
        </FormItem>
        <FormItem>
          {getFieldDecorator('incoming_sms_enabled', {
            valuePropName: 'checked',
            initialValue: connection.incoming_sms_enabled === '1',
          })(
            <Checkbox
              label={t('incoming_sms_enabled', 'Allow Incoming SMS Messages')}
            />,
          )}
        </FormItem>
        <FormItem>
          {getFieldDecorator('incoming_calls_enabled', {
            valuePropName: 'checked',
            initialValue: connection.incoming_calls_enabled === '1',
          })(
            <Checkbox
              label={t('incoming_call_enabled', 'Allow Incoming Calls')}
            />,
          )}
        </FormItem>
        <Typography gutterBottom fontWeight={500}>
          Forward call to
        </Typography>
        <FormItem>
          {getFieldDecorator('fwd_human_receptionist', {
            valuePropName: 'checked',
            initialValue: connection.fwd_human_receptionist === '1',
          })(<Checkbox label={t('forward-human', 'A human receptionist')} />)}
        </FormItem>
        {fwd_human_receptionist && (
          <Box>
            <FormItem>
              {getFieldDecorator('human_receptionist_contact_id', {
                initialValue: connection.human_receptionist_contact_id,
                rules: [
                  {
                    validator: (rule, value, cb) => {
                      if (!value || value === '0') {
                        cb(rule.message);
                      } else {
                        cb();
                      }
                    },
                    message: t('common:field-required'),
                  },
                ],
              })(
                <Select
                  fullWidth
                  transformValue
                  isLoading={loading}
                  options={operators?.map((i) => ({
                    label: i.name,
                    value: i.contact_id,
                  }))}
                  label={t('select-operator-label', 'Select an Operator')}
                  onChange={onCallerChange}
                />,
              )}
            </FormItem>
            <Stack my={2} direction="row" spacing={2} alignItems="center">
              <Typography fontWeight={500}>
                {t('retry-duration-label', 'How long to try before voicemail?')}
              </Typography>
              <FormItem>
                {getFieldDecorator('human_receptionist_ring_timeout', {
                  initialValue: connection.human_receptionist_ring_timeout,
                  rules: [
                    {
                      required: true,
                      message: t('common:field-required'),
                    },
                  ],
                })(
                  <TextInput
                    sx={{ width: '200px' }}
                    margin="none"
                    type="number"
                    endAdornment={
                      <Typography sx={{ pr: 1 }}>Seconds</Typography>
                    }
                  />,
                )}
              </FormItem>
              <FormItem>
                {getFieldDecorator('human_receptionist_confirm_call', {
                  initialValue:
                    connection.human_receptionist_confirm_call === '1',
                  valuePropName: 'checked',
                })(
                  <Switch
                    sx={{ flexShrink: 0 }}
                    labelPlacement="start"
                    margin="none"
                    label={t('announce-caller-label', 'Announce Caller')}
                  />,
                )}
              </FormItem>
            </Stack>
            <FormItem>
              {getFieldDecorator('hours', {
                initialValue: callHourGroup,
                rules: [
                  {
                    required: true,
                    message: t(
                      'talk:add-operating-hours',
                      'Please Add Operating Hours',
                    ),
                  },
                  {
                    validator: timeValidator,
                  },
                ],
              })(<WorkingHoursSelect />)}
            </FormItem>
          </Box>
        )}
        <WithFeature
          feature={FEATURES.TALK_DIGITAL_RECEPTION}
          message={
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Typography
                paragraph
                variant="h4"
                textAlign="center"
                fontWeight={600}
              >
                Add Calling To Supercharge Your Account
              </Typography>
              <Box
                maxWidth={480}
                margin="auto"
                mt={4}
                border={5}
                borderColor="talk.main"
              >
                <VideoPlayer url="https://vimeo.com/805567415" />
              </Box>
            </Paper>
          }
        >
          <FormItem>
            {getFieldDecorator('forwardToDR', {
              valuePropName: 'checked',
              initialValue: forwardToDR,
            })(<Checkbox label={t('forward-dr', 'A digital receptionist')} />)}
          </FormItem>
          {forwardToDR && (
            <Stack direction="row" spacing={2} alignItems="end">
              <FormItem>
                {getFieldDecorator('digital_receptionist_id', {
                  initialValue: connection.digital_receptionist_id,
                  rules: [
                    {
                      validator: (rule, value, cb) => {
                        if (!value || value === '0') {
                          cb(rule.message);
                        } else {
                          cb();
                        }
                      },
                      message: t('common:field-required'),
                    },
                  ],
                })(
                  <Select
                    fullWidth
                    transformValue
                    menuPlacement="top"
                    margin="none"
                    label={t('select-dr-label', 'Assign digital receptionist')}
                    options={receptionists}
                  />,
                )}
              </FormItem>
              <Button variant="text" component={Link} to="/calling/dr">
                Manage
              </Button>
            </Stack>
          )}
        </WithFeature>
        <Box mt={4}>
          <Typography fontWeight={600}>
            {t('after-hours-auto-reply', 'After Hours Auto Reply')}
          </Typography>
          <FormItem>
            {getFieldDecorator('connection_away_message_enabled', {
              valuePropName: 'checked',
              initialValue: connection.connection_away_message_enabled === '1',
            })(
              <Switch
                fullWidth
                id="connection_away_message_enabled"
                label={t('enable-auo-reply', 'Enable Auto Reply')}
                hint={t(
                  'auto-reply-hint',
                  'When enabled, an auto reply will be sent to all incoming messages received during this time.',
                )}
              />,
            )}
          </FormItem>
          <Stack direction="row" spacing={2}>
            <FormItem>
              {getFieldDecorator('connection_away_start_time', {
                initialValue: connection.connection_away_start_time
                  ? dateToMinutes(
                      toUserTimezone(
                        connection.connection_away_start_time,
                        account.account_timezone,
                        'HH:mm:ss',
                      ),
                    )
                  : null,
                rules: [
                  {
                    validator: validateAwayMessage,
                    message: t(
                      'auto-reply-start-required',
                      'Please select start time',
                    ),
                  },
                ],
              })(<TimePicker label={t('start-time', 'Start time')} />)}
            </FormItem>
            <FormItem>
              {getFieldDecorator('connection_away_end_time', {
                initialValue: connection.connection_away_end_time
                  ? dateToMinutes(
                      toUserTimezone(
                        connection.connection_away_end_time,
                        account.account_timezone,
                        'HH:mm:ss',
                      ),
                    )
                  : null,
                rules: [
                  {
                    validator: validateAwayMessage,
                    message: t(
                      'auto-reply-end-time-required',
                      'Please enter end time',
                    ),
                  },
                ],
              })(<TimePicker label={t('end-time', 'End time')} />)}
            </FormItem>
          </Stack>
          <FormItem>
            {getFieldDecorator('connection_away_message', {
              initialValue: connection.connection_away_message,
              rules: [
                {
                  validator: validateAwayMessage,
                  message: t(
                    'auto-reply-msg-required',
                    'Please enter away message',
                  ),
                },
              ],
            })(
              <TextField
                fullWidth
                inputProps={{
                  multiline: true,
                  minRows: 2,
                }}
                label={t('auto-reply-msg', 'Auto Reply Message')}
              />,
            )}
          </FormItem>
        </Box>
      </ModalContent>
      <ModalActions>
        <Button onClick={onCancel} variant="text">
          {t('common:cancel')}
        </Button>
        <Button onClick={handleSubmit}>{t('common:save')}</Button>
      </ModalActions>
    </>
  );
};

export default Form.create()(Edit);
